import { API_BASE_URL, browserSafeFetch } from "./api-base.ts";
import { sharedPublicRead } from "./public-read-cache.ts";
import type { BacktestFormState } from "./types.ts";
import type { CommunityFeedPage } from "./strategy-community.ts";
import { DEFAULT_STRATEGY_PRESET, selectStrategyPreset } from "./strategy-presets.ts";

export const FUTURES_DISCOVERY_TIMEFRAMES = ["30m", "1h", "4h", "1d"] as const;
export type FuturesDiscoveryTimeframe = typeof FUTURES_DISCOVERY_TIMEFRAMES[number];
export interface FuturesMarket {
  id: string; display_symbol: string; display_name: string; group: string; asset_class: string;
  source_provider: string; source_symbol: string; source_kind: string;
  community_symbols: string[]; supported_timeframes: string[]; discovery_timeframes: FuturesDiscoveryTimeframe[];
  backtest_defaults: { symbol: string; provider: string; market: string; execution_model: "futures"; margin_mode: "isolated"; leverage: number };
  proxy: { for_symbol: string; description: string } | null;
}
export interface FuturesMarketCatalog { schema_version: 1; catalog_version: string; items: FuturesMarket[]; freshness?: { state: "live" | "stale" | "bundled" }; has_more?: boolean; total?: number }
export interface FuturesMarketSelection { market: FuturesMarket; timeframe: FuturesDiscoveryTimeframe; requestID: string }
const identifier = /^[a-z0-9-]{1,80}$/;
const text = (value: unknown): value is string => typeof value === "string" && value.length > 0 && value.length <= 300;
const object = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === "object" && !Array.isArray(value));
export function parseFuturesMarketCatalog(value: unknown): FuturesMarketCatalog {
  if (!object(value) || value.schema_version !== 1 || !text(value.catalog_version) || !Array.isArray(value.items) || value.items.length > 1000) throw new Error("invalid_market_catalog");
  const ids = new Set<string>();
  for (const item of value.items) {
    if (!object(item) || !text(item.id) || !identifier.test(item.id) || ids.has(item.id)
      || ["display_symbol", "display_name", "group", "asset_class", "source_provider", "source_symbol", "source_kind"].some((key) => !text(item[key]))
      || !["crypto", "crypto_futures", "metal", "metals", "forex"].includes(String(item.asset_class))
      || !Array.isArray(item.community_symbols) || item.community_symbols.some((value) => !text(value))
      || !Array.isArray(item.supported_timeframes) || item.supported_timeframes.some((frame) => typeof frame !== "string" || !/^\d+[mhdwM]$/.test(frame))
      || !Array.isArray(item.discovery_timeframes) || !item.discovery_timeframes.length || item.discovery_timeframes.some((frame) => !FUTURES_DISCOVERY_TIMEFRAMES.includes(frame) || !(item.supported_timeframes as string[]).includes(frame))
      || !object(item.backtest_defaults) || !text(item.backtest_defaults.symbol) || !text(item.backtest_defaults.provider) || !text(item.backtest_defaults.market)
      || item.backtest_defaults.execution_model !== "futures" || item.backtest_defaults.margin_mode !== "isolated"
      || !["binance_futures", "yfinance"].includes(String(item.source_provider)) || item.backtest_defaults.provider !== item.source_provider
      || item.backtest_defaults.symbol !== item.source_symbol || item.backtest_defaults.leverage !== 1
      || (item.proxy !== null && (!object(item.proxy) || !text(item.proxy.for_symbol) || !text(item.proxy.description)))) throw new Error("invalid_market_catalog");
    ids.add(item.id);
  }
  return value as unknown as FuturesMarketCatalog;
}

/** Live discovery no longer advertises Yahoo sources. Keep the general parser
 * compatible with archived selections and preserve favorite IDs separately. */
export function liveFuturesMarketCatalog(value: unknown): FuturesMarketCatalog {
  const catalog = parseFuturesMarketCatalog(value);
  const items = catalog.items.filter((item) => item.source_provider === "binance_futures");
  if (!items.length) throw new Error("market_catalog_unavailable");
  return { ...catalog, items, ...(catalog.total === undefined ? {} : { total: items.length }) };
}

export async function fetchFuturesMarkets(signal?: AbortSignal, fetcher: typeof fetch = browserSafeFetch, force = false): Promise<FuturesMarketCatalog> {
  return sharedPublicRead("/api/v2/futures-markets", "", async (sharedSignal) => {
    const response = await fetcher(`${API_BASE_URL}/api/v2/futures-markets`, { signal: sharedSignal, credentials: "omit", redirect: "error", headers: { Accept: "application/json" } });
    if (!response.ok || Number(response.headers.get("Content-Length") || 0) > 4 * 1024 * 1024) throw new Error("market_catalog_unavailable");
    const body = await response.text();
    if (body.length > 4 * 1024 * 1024 || sharedSignal.aborted) throw new Error("market_catalog_unavailable");
    return liveFuturesMarketCatalog(JSON.parse(body));
  }, { signal, fetcher, force });
}

/** Only a fresh, exact, complete server query is evidence of an empty market. */
export function hasVerifiedEmptyMarket(page: CommunityFeedPage, selection: FuturesMarketSelection): boolean {
  return page.marketID === selection.market.id && page.selectionMarketID === selection.market.id && page.matchCount === 0 && page.matchComplete === true && page.selectionTimeframe === selection.timeframe
    && page.items.length === 0 && !page.nextCursor;
}

export function futuresMarketBacktestForm(current: BacktestFormState, selection: FuturesMarketSelection): BacktestFormState {
  const { market, timeframe } = selection;
  if (!market.discovery_timeframes.includes(timeframe)) throw new Error("unsupported_market_timeframe");
  return { ...selectStrategyPreset(current, DEFAULT_STRATEGY_PRESET), period: timeframe === "1d" ? "3M" : "1M", startDate: "", endDate: "", symbol: market.backtest_defaults.symbol, timeframe, provider: market.backtest_defaults.provider, market: market.backtest_defaults.market,
    executionModel: "futures", marginMode: "isolated", leverage: market.backtest_defaults.leverage,
    useAI: false, restoredDateRange: undefined };
}

export function futuresMarketName(market: FuturesMarket, locale: string): string {
  if (market.id === "xau-usd") return locale === "vi" ? "Vàng" : "Gold";
  if (market.id === "xag-usd") return locale === "vi" ? "Bạc" : "Silver";
  return market.display_name;
}

export function futuresMarketSearch(markets: FuturesMarket[], query: string): FuturesMarket[] {
  const normalized = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ");
  const terms = normalized(query).trim().split(/\s+/).filter(Boolean);
  return markets.filter((market) => terms.every((term) => normalized(`${market.id} ${market.display_name} ${market.display_symbol} ${market.source_symbol} ${futuresMarketName(market, "vi")}`).split(" ").some((token) => token.startsWith(term))));
}

export interface FuturesMarketPreferences { favorites: string[]; timeframe: FuturesDiscoveryTimeframe }
export function parseFuturesMarketPreferences(value: unknown): FuturesMarketPreferences {
  const raw = object(value) ? value : {};
  return { favorites: Array.isArray(raw.favorites) ? Array.from(new Set(raw.favorites.filter((id): id is string => typeof id === "string" && identifier.test(id)))).slice(0, 20) : [],
    timeframe: FUTURES_DISCOVERY_TIMEFRAMES.includes(raw.timeframe as FuturesDiscoveryTimeframe) ? raw.timeframe as FuturesDiscoveryTimeframe : "1h" };
}


/** A curated starting list, not a live-volume ranking or a saved favorite. */
export const POPULAR_FUTURES_MARKET_IDS = [
  "btc-usdt", "eth-usdt", "sol-usdt", "bnb-usdt", "xrp-usdt", "doge-usdt",
  "ada-usdt", "avax-usdt", "link-usdt", "xau-usd", "xag-usd", "eur-usd", "gbp-usd", "usd-jpy", "aud-usd",
] as const;
export type FuturesMarketGroup = "popular" | "all" | "favorites" | "crypto" | "metals" | "forex";
export function filterFuturesMarkets(markets: FuturesMarket[], query: string, group: FuturesMarketGroup, favorites: readonly string[]): FuturesMarket[] {
  const popular = POPULAR_FUTURES_MARKET_IDS as readonly string[];
  return futuresMarketSearch(markets, query).filter((market) => {
    if (group === "popular") return popular.includes(market.id);
    if (group === "favorites") return favorites.includes(market.id);
    if (group === "crypto") return market.asset_class.startsWith("crypto");
    if (group === "metals") return ["metal", "metals"].includes(market.asset_class);
    return group === "all" || market.asset_class === group;
  }).sort((a, b) => {
    if (group === "popular") return popular.indexOf(a.id) - popular.indexOf(b.id);
    return Number(favorites.includes(b.id)) - Number(favorites.includes(a.id));
  });
}
export function favoriteFuturesMarkets(markets: FuturesMarket[], favorites: readonly string[]): FuturesMarket[] {
  const byID = new Map(markets.map((market) => [market.id, market]));
  return favorites.flatMap((id) => byID.has(id) ? [byID.get(id)!] : []).slice(0, 20);
}
